/**
 * Pure form mapping and validation for normalized intrusion configuration.
 *
 * Contains:
 * - initial-value and blade-section builders
 * - contract-aware validation
 * - sparse PATCH generation and error-path mapping
 *
 * Returns plain JavaScript and performs no JSX rendering or network I/O.
 */
import * as yup from 'yup';

import buildRenderedPatchKnownFields from '@sophocles/views/sites/components/SiteDashboard/configPatchErrorFields';

export const INTRUSION_SCHEMA_VERSION = 1;
export const INTRUSION_RELAY_ACTION = Object.freeze({ NONE: 0, ON: 1, OFF: 2 });

const INTRUSION_SETTING_FIELDS = Object.freeze([
  'delay_before_relays_power_off_seconds',
  'automatic_rearm_time_minutes',
  'automatic_alarm_cancel_time_minutes',
  'relay_actions',
]);

const INTRUSION_INPUT_FIELDS = Object.freeze([
  'name',
  'enabled',
  'polarity',
  'alarm_delay_seconds',
  'reactivation_delay_minutes',
  'relay_actions',
]);

const NUMERIC_FIELDS = new Set([
  'delay_before_relays_power_off_seconds',
  'automatic_rearm_time_minutes',
  'automatic_alarm_cancel_time_minutes',
  'alarm_delay_seconds',
  'reactivation_delay_minutes',
]);

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value ?? {}, key);
const editableFieldSet = section => new Set(Array.isArray(section?.editable_fields) ? section.editable_fields : []);

function copyRelayActions(relayActions) {
  return Object.fromEntries(Object.entries(relayActions ?? {}).map(([relayNumber, action]) => [relayNumber, action]));
}

function copyPresentFields(source, fields) {
  return fields.reduce((result, field) => {
    if (!hasOwn(source, field)) return result;
    return {
      ...result,
      [field]: field === 'relay_actions' ? copyRelayActions(source[field]) : source[field],
    };
  }, {});
}

function sortedInputs(inputs) {
  return [...(inputs ?? [])].sort((left, right) => left.input_number - right.input_number);
}

/**
 * @param {object|null} document - normalized intrusion configuration document
 * @returns {{settings: object, inputs: object[]}} Formik-ready values
 */
export function buildIntrusionInitialValues(document) {
  return {
    settings: copyPresentFields(document?.settings, INTRUSION_SETTING_FIELDS),
    inputs: sortedInputs(document?.inputs).map(input => ({
      key: input.key,
      input_number: input.input_number,
      ...copyPresentFields(input, INTRUSION_INPUT_FIELDS),
    })),
  };
}

/**
 * Pair Formik values with their immutable contract sections for rendering.
 *
 * @param {object|null} document - normalized intrusion configuration document
 * @param {{settings?: object, inputs?: object[]}} values - current Formik values
 * @returns {{settings: ({contract: object, values: object}|null), inputs: object[]}} renderable blade sections
 */
export function resolveIntrusionBladeSections(document, values) {
  if (!document) return { settings: null, inputs: [] };

  const settingsValues = values?.settings ?? {};
  const settings =
    document.settings && Object.keys(settingsValues).length > 0
      ? { contract: document.settings, values: settingsValues }
      : null;
  const inputContractByKey = new Map((document.inputs ?? []).map(input => [input.key, input]));
  const inputs = (values?.inputs ?? []).flatMap((inputValues, inputIndex) => {
    const contract = inputContractByKey.get(inputValues.key);
    return contract ? [{ key: inputValues.key, inputIndex, contract, values: inputValues }] : [];
  });

  return { settings, inputs };
}

function numberSchema(messages) {
  return yup
    .number()
    .typeError(messages.number)
    .integer(messages.wholeNumber)
    .min(0, messages.range)
    .max(255, messages.range)
    .required(messages.required);
}

function relayActionsSchema(section, messages) {
  const relayShape = Object.keys(section?.relay_actions ?? {}).reduce(
    (shape, relayNumber) => ({
      ...shape,
      [relayNumber]: yup
        .number()
        .oneOf(Object.values(INTRUSION_RELAY_ACTION), messages.relayAction)
        .required(messages.required),
    }),
    {},
  );
  return yup.object(relayShape);
}

function fieldSchema(field, section, messages) {
  if (NUMERIC_FIELDS.has(field)) return numberSchema(messages);
  if (field === 'name') return yup.string().trim().required(messages.required).max(50, messages.nameLength);
  if (field === 'enabled' || field === 'polarity') return yup.boolean().required(messages.required);
  if (field === 'relay_actions') return relayActionsSchema(section, messages);
  return yup.mixed().notRequired();
}

function inputSchema(input, permissions, messages) {
  return yup
    .object({
      key: yup.string().required(messages.required),
      ...editableShape(input, INTRUSION_INPUT_FIELDS, permissions?.can_edit_inputs, messages),
    })
    .test('action-before-reactivation', messages.actionDelayLessThanReactivation, function (value) {
      const actionDelaySeconds = Number(value?.alarm_delay_seconds);
      const reactivationDelayMinutes = Number(value?.reactivation_delay_minutes);
      if (
        !value?.enabled ||
        !Number.isFinite(actionDelaySeconds) ||
        !Number.isFinite(reactivationDelayMinutes) ||
        reactivationDelayMinutes === 0 ||
        actionDelaySeconds / 60 < reactivationDelayMinutes
      ) {
        return true;
      }
      return this.createError({
        path: `${this.path}.alarm_delay_seconds`,
        message: messages.actionDelayLessThanReactivation,
      });
    });
}

function editableShape(section, supportedFields, canEdit, messages) {
  if (!canEdit) return {};
  const editable = editableFieldSet(section);
  return supportedFields.reduce((shape, field) => {
    if (!editable.has(field) || !hasOwn(section, field)) return shape;
    return { ...shape, [field]: fieldSchema(field, section, messages) };
  }, {});
}

/**
 * @param {object} document - normalized intrusion configuration document
 * @param {{can_edit_settings?: boolean, can_edit_inputs?: boolean}} permissions - route-scoped write gates
 * @param {object} messages - translated validation messages
 * @returns {yup.ObjectSchema} schema for buildIntrusionInitialValues output
 */
export function buildIntrusionValidationSchema(document, permissions, messages) {
  const inputByKey = new Map((document?.inputs ?? []).map(input => [input.key, input]));

  return yup.object({
    settings: yup.object(
      editableShape(document?.settings, INTRUSION_SETTING_FIELDS, permissions?.can_edit_settings, messages),
    ),
    inputs: yup.array().of(yup.lazy(value => inputSchema(inputByKey.get(value?.key), permissions, messages))),
  });
}

function normalizedValue(field, value) {
  if (NUMERIC_FIELDS.has(field)) return value === '' || value == null ? value : Number(value);
  return value;
}

function changedFields(section, values, supportedFields) {
  const editable = editableFieldSet(section);
  return supportedFields.reduce((changes, field) => {
    if (!editable.has(field) || !hasOwn(section, field) || !hasOwn(values, field)) return changes;

    if (field === 'relay_actions') {
      const relayActions = Object.entries(values[field] ?? {}).reduce((result, [relayNumber, action]) => {
        const nextAction = Number(action);
        const currentAction = Number(section[field]?.[relayNumber]);
        return nextAction === currentAction ? result : { ...result, [relayNumber]: nextAction };
      }, {});
      return Object.keys(relayActions).length > 0 ? { ...changes, relay_actions: relayActions } : changes;
    }

    const nextValue = normalizedValue(field, values[field]);
    const currentValue = normalizedValue(field, section[field]);
    if (nextValue === currentValue) return changes;
    return { ...changes, [field]: nextValue };
  }, {});
}

/**
 * @param {object} document - source normalized intrusion configuration document
 * @param {{settings: object, inputs: object[]}} values - current Formik values
 * @param {{can_edit_settings?: boolean, can_edit_inputs?: boolean}} permissions - route-scoped write gates
 * @returns {object} minimal PATCH body with schema_version and changed editable fields
 */
export function generateIntrusionConfigPatch(document, values, permissions = {}) {
  const patch = { schema_version: document.schema_version };

  if (permissions.can_edit_settings) {
    const settings = changedFields(document?.settings, values?.settings, INTRUSION_SETTING_FIELDS);
    if (Object.keys(settings).length > 0) patch.settings = settings;
  }

  if (permissions.can_edit_inputs) {
    const valuesByKey = new Map((values?.inputs ?? []).map(input => [input.key, input]));
    const inputs = (document?.inputs ?? []).flatMap(input => {
      const inputValues = valuesByKey.get(input.key);
      if (!inputValues) return [];
      const changes = changedFields(input, inputValues, INTRUSION_INPUT_FIELDS);
      return Object.keys(changes).length > 0 ? [{ key: input.key, ...changes }] : [];
    });
    if (inputs.length > 0) patch.inputs = inputs;
  }

  return patch;
}

/**
 * @param {object|null} patch generated management PATCH
 * @returns {boolean} whether the document contains a field update
 */
export function hasIntrusionConfigChanges(patch) {
  return Boolean(patch?.settings || patch?.inputs);
}

/**
 * @param {object} patch sparse generated management PATCH
 * @returns {string[]} API paths backed by rendered form controls
 */
export function buildIntrusionPatchKnownFields(patch) {
  return buildRenderedPatchKnownFields(patch);
}

/**
 * @param {string} path backend validation path
 * @param {object} patch sparse generated management PATCH
 * @param {{inputs?: object[]}} values current Formik values
 * @returns {string} matching full Formik field path
 */
export function mapIntrusionPatchErrorPath(path, patch, values) {
  const match = path.match(/^inputs\.(\d+)(\..+)?$/);
  if (!match) return path;
  const patchInput = patch.inputs?.[Number(match[1])];
  const formIndex = (values.inputs ?? []).findIndex(input => input.key === patchInput?.key);
  return formIndex >= 0 ? `inputs.${formIndex}${match[2] ?? ''}` : path;
}
